import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

/**
 * One application (per-word) row for STATUS-stage leads. Drives the per-word chip routing
 * in the pending-items table — each entry routes to its sub-step (Formalities Check / Advertised / etc.).
 */
export interface NewApplicationListItemMark {
  applicationId: string;
  tmNo: string;
  mark: string;
  typeOfMark: string | null;
  classNumber: number | null;
  status: string | null;
}

/** Single application row from the list API */
export interface NewApplicationListItem {
  id: string;
  leadNo: string;
  clientName: string;
  email: string | null;
  phone: string | null;
  status: string;
  stage: string | null;
  markName: string | null;
  /**
   * Pre-formatted mark for display. For STATUS-stage rows this includes the type and class
   * in brackets — e.g. "Apple (Label, 25 / Word, 35)". Falls back to plain markName otherwise.
   */
  markDisplay?: string | null;
  /**
   * Per-application breakdown for STATUS-stage leads. Empty/omitted for other stages.
   * Used to render clickable per-word chips that route by each word's current status.
   */
  marks?: NewApplicationListItemMark[];
  createdAt: string;
}

/** Paginated response from list applications API */
export interface ListApplicationsResponse {
  items: NewApplicationListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success?: false;
  message?: string | string[];
  error?: string;
  statusCode?: number;
}

function getApiErrorMessage(json: ApiErrorResponse | undefined, fallback: string): string {
  if (!json) return fallback;
  if (Array.isArray(json.message)) {
    return json.message.map(String).filter(Boolean).join(" ");
  }
  if (typeof json.message === "string" && json.message.trim()) {
    return json.message;
  }
  if (typeof json.error === "string" && json.error.trim()) {
    return json.error;
  }
  return fallback;
}

export type NewApplicationStageFilter = "DISCUSSION" | "FILING" | "STATUS";
export type NewApplicationSort = "newest" | "oldest";

export interface ListApplicationsParams {
  page?: number;
  limit?: number;
  search?: string;
  stage?: NewApplicationStageFilter;
  sort?: NewApplicationSort;
}

export async function listApplications(
  pageOrParams: number | ListApplicationsParams = 1,
  limit = 14,
  search?: string
): Promise<ListApplicationsResponse> {
  const opts: ListApplicationsParams =
    typeof pageOrParams === "number"
      ? { page: pageOrParams, limit, search }
      : pageOrParams;

  const params = new URLSearchParams({
    page: String(opts.page ?? 1),
    limit: String(opts.limit ?? 14),
  });
  if (opts.search?.trim()) params.set("search", opts.search.trim());
  if (opts.stage) params.set("stage", opts.stage);
  if (opts.sort) params.set("sort", opts.sort);

  const res = await apiFetch(`${API_BASE}/new-app/leads?${params}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as ApiSuccessResponse<ListApplicationsResponse> | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(getApiErrorMessage(json as ApiErrorResponse, "Failed to load applications"));
  }

  const data = (json as ApiSuccessResponse<ListApplicationsResponse>).data;
  return data;
}

/**
 * Same as listApplications but only returns leads past the Acknowledgement to Client step
 * (S2_14). Backed by GET /new-app/leads/acknowledged.
 */
export async function listAcknowledgedApplications(
  pageOrParams: number | ListApplicationsParams = 1,
  limit = 14,
  search?: string
): Promise<ListApplicationsResponse> {
  const opts: ListApplicationsParams =
    typeof pageOrParams === "number"
      ? { page: pageOrParams, limit, search }
      : pageOrParams;

  const params = new URLSearchParams({
    page: String(opts.page ?? 1),
    limit: String(opts.limit ?? 14),
  });
  if (opts.search?.trim()) params.set("search", opts.search.trim());
  if (opts.stage) params.set("stage", opts.stage);
  if (opts.sort) params.set("sort", opts.sort);

  const res = await apiFetch(`${API_BASE}/new-app/leads/acknowledged?${params}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as ApiSuccessResponse<ListApplicationsResponse> | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to load acknowledged applications")
    );
  }

  return (json as ApiSuccessResponse<ListApplicationsResponse>).data;
}

/** Workflow status from GET /new-app/leads/:id/status */
export interface WorkflowStatusResponse {
  currentStep: string | null;
  currentSection: string | null;
  maxStepReached: string | null;
  leadStage: string | null;
  steps: Array<{
    slug: string;
    label: string;
    stage: string;
    stepType: string;
    parentStep?: string;
    isCompleted: boolean;
    isSkipped: boolean;
  }>;
  mainStepsWithSubSteps: unknown[];
}

const workflowStatusInFlight = new Map<string, Promise<WorkflowStatusResponse>>();

async function fetchWorkflowStatus(
  leadId: string,
  tmNo: string | null
): Promise<WorkflowStatusResponse> {
  const url = tmNo
    ? `${API_BASE}/new-app/${leadId}/status?tmNo=${encodeURIComponent(tmNo)}`
    : `${API_BASE}/new-app/${leadId}/status`;
  const res = await apiFetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as ApiSuccessResponse<WorkflowStatusResponse> | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(getApiErrorMessage(json as ApiErrorResponse, "Failed to load workflow status"));
  }

  return (json as ApiSuccessResponse<WorkflowStatusResponse>).data;
}

/**
 * Returns the workflow status for a lead. When `tmNo` is supplied, progress is
 * scoped to that TM (per-TM step rows only) so deep-linking from per-TM
 * dashboard rows lands on the right substep. Concurrent calls for the same
 * (leadId, tmNo) share a single in-flight network request (dedupe); once the
 * request settles the entry is cleared, so subsequent calls re-fetch fresh data.
 */
export async function getWorkflowStatus(
  leadId: string,
  tmNo?: string | null
): Promise<WorkflowStatusResponse> {
  const normalizedTmNo = tmNo?.trim() || null;
  const key = normalizedTmNo ? `${leadId}|${normalizedTmNo}` : leadId;
  const existing = workflowStatusInFlight.get(key);
  if (existing) return existing;

  const promise = fetchWorkflowStatus(leadId, normalizedTmNo).finally(() => {
    if (workflowStatusInFlight.get(key) === promise) {
      workflowStatusInFlight.delete(key);
    }
  });
  workflowStatusInFlight.set(key, promise);
  return promise;
}

/** Step values for skip-step API (must match backend NewApplicationStep enum) */
export const NewApplicationStep = {
  S1_04_AdvanceInvoiceToClient: "S1_04_ADVANCE_INVOICE_TO_CLIENT",
  S1_05_AdvanceAmountFromClient: "S1_05_ADVANCE_AMOUNT_FROM_CLIENT",
  S1_12_PaymentFromClient: "S1_12_PAYMENT_FROM_CLIENT",
  S2_07_PoaPreparation: "S2_07_POA_PREPARATION",
  S2_17_PaymentFromClientExpedited: "S2_17_PAYMENT_FROM_CLIENT_EXPEDITED",
  S3_03b_vii_PaymentFromClientReplyToObjection:
    "S3_03B_VII_PAYMENT_FROM_CLIENT_REPLY_TO_OBJECTION",
  S3_03b_viii_DocumentsFromClientReplyToObjection:
    "S3_03B_VIII_DOCUMENTS_FROM_CLIENT_REPLY_TO_OBJECTION",
  S3_03d_vii_a_SaveRevisedInvoiceHearing1:
    "S3_03D_VII_A_SAVE_REVISED_INVOICE_HEARING_1",
  S3_03d_viii_a_SaveInvoiceTmMBeforeHearing:
    "S3_03D_VIII_A_SAVE_INVOICE_TM_M_BEFORE_HEARING",
  S3_03d_xii_PaymentFromClientHearing:
    "S3_03D_XII_PAYMENT_FROM_CLIENT_HEARING",
  S3_03d_xv_DocumentsFromClientHearing:
    "S3_03D_XV_DOCUMENTS_FROM_CLIENT_HEARING",
  S3_03d_xxv_UploadDocumentsAfterHearing:
    "S3_03D_XXV_UPLOAD_DOCUMENTS_AFTER_HEARING",
  S3_03d_xxvi_a_SaveInvoiceTmMAfterHearing:
    "S3_03D_XXVI_A_SAVE_INVOICE_TM_M_AFTER_HEARING",
  S3_04a_vii_PaymentFromClientCounter:
    "S3_04A_VII_PAYMENT_FROM_CLIENT_COUNTER",
  S3_04a_xi_DateOfNoticeOfCounterStatement:
    "S3_04A_XI_DATE_OF_NOTICE_OF_COUNTER_STATEMENT",
  S3_04a_xxii_DocumentsFromClientForEvidence:
    "S3_04A_XXII_DOCUMENTS_FROM_CLIENT_FOR_EVIDENCE",
  S3_04a_xxxvi_a_SaveInvoiceTmMBeforeOppositionHearing:
    "S3_04A_XXXVI_A_SAVE_INVOICE_TM_M_BEFORE_OPPOSITION_HEARING",
  S3_04a_xl_PaymentFromClientOppositionHearing:
    "S3_04A_XL_PAYMENT_FROM_CLIENT_OPPOSITION_HEARING",
  S3_04a_xli_DocumentsFromClientOppositionHearing:
    "S3_04A_XLI_DOCUMENTS_FROM_CLIENT_OPPOSITION_HEARING",
  S3_04a_xlvi_a_SaveInvoiceTmMAfterOppositionHearing:
    "S3_04A_XLVI_A_SAVE_INVOICE_TM_M_AFTER_OPPOSITION_HEARING",
  S3_04a_xlvii_PaymentFromClientTmMAfterOppositionHearing:
    "S3_04A_XLVII_PAYMENT_FROM_CLIENT_TM_M_AFTER_OPPOSITION_HEARING",
} as const;

export interface SkipStepResponse {
  progressedTo: string;
}

/** Skip a step (Progress button) - POST /new-app/:id/skip-step */
export async function skipStep(
  leadId: string,
  step: string
): Promise<SkipStepResponse> {
  const res = await apiFetch(`${API_BASE}/new-app/${leadId}/skip-step`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ step }),
  });

  const json = (await res.json()) as ApiSuccessResponse<SkipStepResponse> | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(getApiErrorMessage(json as ApiErrorResponse, "Failed to skip step"));
  }

  return (json as ApiSuccessResponse<SkipStepResponse>).data;
}

export interface ReassignLeadPayload {
  toUserId: string;
  reason?: string;
}

/** Reassign lead owner - POST /new-app/:id/assign */
export async function assignLead(leadId: string, payload: ReassignLeadPayload): Promise<string> {
  const res = await apiFetch(`${API_BASE}/new-app/${leadId}/assign`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(getApiErrorMessage(json as ApiErrorResponse, "Failed to reassign lead"));
  }

  return (json as ApiSuccessResponse<unknown>).message || "Lead reassigned successfully";
}
